import { useEffect, useState } from 'react';
import { Check, Copy, Unlock } from 'lucide-react';
import { EntityModal } from '../common/EntityModal';
import { useReopenQuestionnaire } from '../../hooks/useQuestionnaires';

export interface ReopenQuestionnaireModalProps {
  open: boolean;
  onClose: () => void;
  questionnaireId: string | null;
  /** Address the questionnaire was last sent to — prefills the notice email. */
  sentTo?: string | null;
}

/**
 * Reopen a submitted questionnaire: the client can change their answers, fill
 * in what they skipped, and submit again. Optionally emails them the fill link
 * with a note about what needs updating.
 */
export function ReopenQuestionnaireModal({
  open,
  onClose,
  questionnaireId,
  sentTo,
}: ReopenQuestionnaireModalProps) {
  const [email, setEmail] = useState(sentTo ?? '');
  const [note, setNote] = useState('');
  const [fillUrl, setFillUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const reopen = useReopenQuestionnaire();

  // The modal stays mounted on list pages — refresh the prefill on each open.
  useEffect(() => {
    if (open) {
      setEmail(sentTo ?? '');
      setNote('');
      setFillUrl(null);
      setCopied(false);
    }
  }, [open, sentTo]);

  function handleReopen(notifyClient: boolean) {
    if (!questionnaireId) return;
    reopen.mutate(
      {
        id: questionnaireId,
        email: notifyClient ? email.trim() || undefined : undefined,
        note: notifyClient ? note.trim() || undefined : undefined,
      },
      { onSuccess: (data) => setFillUrl(data.fillUrl) },
    );
  }

  function handleClose() {
    setFillUrl(null);
    setCopied(false);
    onClose();
  }

  return (
    <EntityModal open={open} onClose={handleClose} title="Reopen Questionnaire" size="sm">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          The client can change their answers, add the ones they skipped, and submit again. Existing
          answers are kept — nothing is cleared.
        </p>
        <div>
          <label htmlFor="reopen-email" className="block text-sm font-medium text-gray-700 mb-1">
            Client email (optional)
          </label>
          <input
            id="reopen-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="client@example.com"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="reopen-note" className="block text-sm font-medium text-gray-700 mb-1">
            Note to the client (optional)
          </label>
          <textarea
            id="reopen-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="What should they update?"
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => handleReopen(true)}
            disabled={reopen.isPending || !email.trim()}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300"
            data-testid="reopen-notify-button"
          >
            <Unlock size={14} />
            {reopen.isPending ? 'Reopening…' : 'Reopen & Email Client'}
          </button>
          <button
            onClick={() => handleReopen(false)}
            disabled={reopen.isPending}
            className="px-4 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            data-testid="reopen-only-button"
          >
            Reopen Only
          </button>
        </div>
        {reopen.isError && (
          <p className="text-xs text-red-600" data-testid="reopen-error">
            {reopen.error instanceof Error ? reopen.error.message : 'Failed to reopen'}
          </p>
        )}
        {fillUrl && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg space-y-2">
            <p className="text-xs text-green-800" data-testid="reopen-success">
              Reopened — the client can edit and submit again with this link:
            </p>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={fillUrl}
                className="flex-1 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={() => {
                  void navigator.clipboard.writeText(fillUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
                className="p-2 text-gray-500 hover:text-primary-600 border border-gray-200 rounded-lg bg-white"
                aria-label="Copy link"
                data-testid="reopen-copy-link-button"
              >
                {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
              </button>
            </div>
          </div>
        )}
      </div>
    </EntityModal>
  );
}
